import axios from "axios";
import { getApiBaseUrl } from "./apiBase";

// Try different import patterns for scorm-again
let SCORM;
try {
  const scormAgain = require("scorm-again");
  SCORM = scormAgain;
} catch (e) {
  try {
    SCORM = require("scorm-again").default;
  } catch (e2) {
    console.error("Failed to import scorm-again:", e2);
  }
}

// Store tracking context globally
let trackingContext = {
  user_id: null,
  course_id: null,
  module_id: null
};

// Cache bookmark data until API is ready/initialized
let pendingBookmark = null;
let apiInitialized = false;

export function initSCORM(versionOrConfig = "1.2", configParam = {}) {
  // Handle both patterns: initSCORM(config) or initSCORM(version, config)
  let version = "1.2";
  let config = {};
  
  if (typeof versionOrConfig === 'object' && versionOrConfig !== null) {
    // Called as initSCORM(config) - config object passed as first param
    config = versionOrConfig;
    version = config.version || "1.2";
  } else {
    // Called as initSCORM(version, config) - traditional pattern
    version = versionOrConfig || "1.2";
    config = configParam || {};
  }
  
  // Store tracking context if provided (always set, even if null/undefined to clear previous values)
  if (config.user_id !== undefined) trackingContext.user_id = config.user_id;
  if (config.course_id !== undefined) trackingContext.course_id = config.course_id;
  if (config.module_id !== undefined) trackingContext.module_id = config.module_id;
  
  // Debug log to verify context is being set
  console.log('📝 Setting tracking context:', {
    user_id: trackingContext.user_id,
    course_id: trackingContext.course_id,
    module_id: trackingContext.module_id,
    from_config: config
  });
  
  // Store version and API reference
  currentVersion = version;
  let API;
  
  apiInitialized = false;

  try {
    if (version === "1.2") {
      if (SCORM && SCORM.createScorm12API) {
        API = SCORM.createScorm12API();
      } else if (SCORM && SCORM.Scorm12API) {
        API = new SCORM.Scorm12API();
      } else if (window.ScormApi) {
        API = new window.ScormApi();
      } else {
        API = createMockScormAPI();
      }
    } else {
      if (SCORM && SCORM.createScorm2004API) {
        API = SCORM.createScorm2004API();
      } else if (SCORM && SCORM.Scorm2004API) {
        API = new SCORM.Scorm2004API();
      } else {
        API = createMockScormAPI();
      }
    }
  } catch (error) {
    console.warn("Using mock SCORM API due to error:", error);
    API = createMockScormAPI();
  }

  // Apply bookmark right after successful initialize
  const originalInitialize = version === "2004" ? API.Initialize : API.LMSInitialize;
  if (originalInitialize) {
    if (version === "2004") {
      API.Initialize = (param) => {
        const result = originalInitialize.call(API, param);
        if (result === "true") {
          apiInitialized = true;
          applyPendingBookmark(API, version);
        }
        return result;
      };
    } else {
      API.LMSInitialize = (param) => {
        const result = originalInitialize.call(API, param);
        if (result === "true") {
          apiInitialized = true;
          applyPendingBookmark(API, version);
        }
        return result;
      };
    }
  }

  // Override LMSCommit/Commit to send tracking data
  const originalCommit = version === "2004" ? API.Commit : API.LMSCommit;
  if (originalCommit) {
    if (version === "2004") {
      API.Commit = (param) => {
        const result = originalCommit.call(API, param);
        const data = collectData(API, version);
        
        // Validate tracking context before sending
        if (!data.user_id || !data.course_id) {
          console.error('❌ Cannot send SCORM data - missing tracking context:', {
            user_id: data.user_id,
            course_id: data.course_id,
            module_id: data.module_id,
            trackingContext: trackingContext
          });
          return result;
        }
        
        axios.post(`${getApiBaseUrl()}/scorm-data`, data)
          .then(() => console.log("📡 SCORM Data Sent", { user_id: data.user_id, course_id: data.course_id }))
          .catch(err => console.error("Error sending SCORM data:", err, data));
        return result;
      };
    } else {
      API.LMSCommit = (param) => {
        const result = originalCommit.call(API, param);
        const data = collectData(API, version);
        
        // Validate tracking context before sending
        if (!data.user_id || !data.course_id) {
          console.error('❌ Cannot send SCORM data - missing tracking context:', {
            user_id: data.user_id,
            course_id: data.course_id,
            module_id: data.module_id,
            trackingContext: trackingContext
          });
          return result;
        }
        
        axios.post(`${getApiBaseUrl()}/scorm-data`, data)
          .then(() => console.log("📡 SCORM Data Sent", { user_id: data.user_id, course_id: data.course_id }))
          .catch(err => console.error("Error sending SCORM data:", err, data));
        return result;
      };
    }
  }

  window.API = API;         // SCORM 1.2
  window.API_1484_11 = API; // SCORM 2004

  // Store API reference
  currentAPI = API;

  // Log tracking context after setting
  console.log('✅ SCORM initialized with tracking context:', {
    user_id: trackingContext.user_id,
    course_id: trackingContext.course_id,
    module_id: trackingContext.module_id,
    version: currentVersion
  });

  return API;
}

// Create a mock SCORM API for testing/fallback
function createMockScormAPI() {
  const mockData = {};
  
  return {
    LMSInitialize: () => "true",
    LMSFinish: () => "true",
    LMSGetValue: (element) => mockData[element] || "",
    LMSSetValue: (element, value) => {
      mockData[element] = value;
      return "true";
    },
    LMSCommit: () => "true",
    LMSGetLastError: () => "0",
    LMSGetErrorString: () => "",
    LMSGetDiagnostic: () => "",
    GetValue: (element) => mockData[element] || "",
    SetValue: (element, value) => {
      mockData[element] = value;
      return "true";
    },
    Commit: function(param) {
      return this.LMSCommit(param);
    },
    GetLastError: () => "0",
    GetErrorString: () => "",
    GetDiagnostic: () => "",
    Initialize: function(param) {
      return this.LMSInitialize(param);
    },
    Terminate: function(param) {
      return this.LMSFinish(param);
    }
  };
}

function collectData(API, version) {
  const getValue = (element) => {
    try {
      return API.LMSGetValue ? API.LMSGetValue(element) : API.GetValue ? API.GetValue(element) : "";
    } catch (e) {
      return "";
    }
  };

  // Log tracking context for debugging
  if (!trackingContext.user_id || !trackingContext.course_id) {
    console.warn('⚠️ Tracking context missing:', trackingContext);
  }

  let data = {
    version,
    // Include tracking context (user_id, course_id, module_id)
    user_id: trackingContext.user_id || null,
    course_id: trackingContext.course_id || null,
    module_id: trackingContext.module_id || null,
    // SCORM data fields
    lesson_location: getValue("cmi.core.lesson_location") || getValue("cmi.location") || "",
    lesson_status: getValue("cmi.core.lesson_status") || getValue("cmi.completion_status") || "incomplete",
    score_raw: getValue("cmi.core.score.raw") || getValue("cmi.score.raw") || "",
    score_min: getValue("cmi.core.score.min") || getValue("cmi.score.min") || "",
    score_max: getValue("cmi.core.score.max") || getValue("cmi.score.max") || "",
    session_time: getValue("cmi.core.session_time") || getValue("cmi.session_time") || "",
    total_time: getValue("cmi.core.total_time") || getValue("cmi.total_time") || "",
    suspend_data: getValue("cmi.suspend_data") || "",
    exit_status: getValue("cmi.core.exit") || getValue("cmi.exit") || "",
    progress_measure: getValue("cmi.progress_measure") || null
  };
  return data;
}

// Store API reference globally
let currentAPI = null;
let currentVersion = '1.2';

// Export helper functions for compatibility
export function commit() {
  return new Promise((resolve) => {
    try {
      if (currentAPI) {
        const commitMethod = currentVersion === '2004' ? currentAPI.Commit : currentAPI.LMSCommit;
        if (commitMethod) {
          const result = commitMethod.call(currentAPI, '');
          // Wait a bit to ensure tracking data is sent
          setTimeout(() => resolve(result), 100);
        } else {
          resolve('false');
        }
      } else {
        resolve('false');
      }
    } catch (error) {
      console.error('Commit error:', error);
      resolve('false');
    }
  });
}

export function finish() {
  return new Promise((resolve) => {
    try {
      if (currentAPI) {
        // First commit to save tracking data before finishing
        commit()
          .then(() => {
            // Then terminate the session
            const finishMethod = currentVersion === '2004' ? currentAPI.Terminate : currentAPI.LMSFinish;
            if (finishMethod) {
              const result = finishMethod.call(currentAPI, '');
              resolve(result);
            } else {
              resolve('false');
            }
          })
          .catch((error) => {
            console.error('Commit error in finish:', error);
            // Still try to finish even if commit fails
            const finishMethod = currentVersion === '2004' ? currentAPI.Terminate : currentAPI.LMSFinish;
            if (finishMethod) {
              const result = finishMethod.call(currentAPI, '');
              resolve(result);
            } else {
              resolve('false');
            }
          });
      } else {
        resolve('false');
      }
    } catch (error) {
      console.error('Finish error:', error);
      resolve('false');
    }
  });
}

// Restore bookmark state to SCORM API
export function restoreBookmark(bookmarkData) {
  if (!bookmarkData) {
    console.warn('⚠️ Cannot restore bookmark - data missing');
    return false;
  }

  if (!currentAPI) {
    pendingBookmark = bookmarkData;
    console.log('⏳ Bookmark queued - SCORM API not created yet:', bookmarkData);
    return true;
  }

  if (!apiInitialized) {
    pendingBookmark = bookmarkData;
    console.log('⏳ Bookmark queued - waiting for LMSInitialize:', bookmarkData);
    return true;
  }

  const restored = restoreBookmarkInternal(currentAPI, currentVersion, bookmarkData);
  if (!restored) {
    pendingBookmark = bookmarkData;
    console.warn('⚠️ Bookmark restore failed - will retry after next initialize');
  }
  return restored;
}

function restoreBookmarkInternal(API, version, bookmarkData) {
  if (!API || !bookmarkData) {
    return false;
  }

  try {
    if (version === '2004') {
      if (bookmarkData.lesson_location) {
        API.SetValue('cmi.location', bookmarkData.lesson_location);
      }
      if (bookmarkData.lesson_status) {
        API.SetValue('cmi.completion_status', bookmarkData.lesson_status);
      }
      if (bookmarkData.suspend_data) {
        API.SetValue('cmi.suspend_data', bookmarkData.suspend_data);
      }
      if (bookmarkData.progress_measure !== null && bookmarkData.progress_measure !== undefined) {
        API.SetValue('cmi.progress_measure', String(bookmarkData.progress_measure));
      }
    } else {
      if (bookmarkData.lesson_location) {
        API.LMSSetValue('cmi.core.lesson_location', bookmarkData.lesson_location);
      }
      if (bookmarkData.lesson_status) {
        API.LMSSetValue('cmi.core.lesson_status', bookmarkData.lesson_status);
      }
      if (bookmarkData.suspend_data) {
        API.LMSSetValue('cmi.suspend_data', bookmarkData.suspend_data);
      }
    }
    console.log('✅ Bookmark restored:', bookmarkData);
    return true;
  } catch (error) {
    console.error('❌ Error restoring bookmark:', error);
    return false;
  }
}

function applyPendingBookmark(API, version) {
  if (!pendingBookmark) {
    return;
  }

  const bookmarkToApply = pendingBookmark;
  pendingBookmark = null;
  const restored = restoreBookmarkInternal(API, version, bookmarkToApply);
  if (!restored) {
    console.warn('⚠️ Failed to apply pending bookmark, will re-queue');
    pendingBookmark = bookmarkToApply;
  } else {
    console.log('📘 Pending bookmark applied after initialization');
  }
}

// Export API wrapper for backward compatibility
export default {
  get api() {
    return currentAPI || (currentVersion === '2004' ? window.API_1484_11 : window.API);
  },
  get version() {
    return currentVersion;
  }
};
